import React from "react";
import {GameItems} from "../State/GameData";
import {L} from "./AssemblyStyles";
import {AssemblyNode, AssemblySetup, ConnectionDraft} from "./AssemblyTypes";
import {AssemblyRecipe} from "../State/AssemblyRecipes";

type ConnectionOptions = {
    from: AssemblyNode[];
    to: AssemblyNode[];
};

type AssemblyConnectionsProps = {
    activeSetup: AssemblySetup;
    connectionDraft: ConnectionDraft;
    setConnectionDraft: React.Dispatch<React.SetStateAction<ConnectionDraft>>;
    connectionOptions: ConnectionOptions;
    createConnection: (fromId: string, toId: string) => void;
    removeConnection: (connectionId: string) => void;
    recipeLookup: Record<string, AssemblyRecipe>;
};

export function AssemblyConnections({
    activeSetup,
    connectionDraft,
    setConnectionDraft,
    connectionOptions,
    createConnection,
    removeConnection,
    recipeLookup,
}: AssemblyConnectionsProps) {
    return (
        <>
            <L.ConnectionForm>
                <label>
                    From
                    <L.Select value={connectionDraft.fromId} onChange={e => setConnectionDraft(draft => ({...draft, fromId: e.target.value}))}>
                        <option value="">Select source</option>
                        {connectionOptions.from.map(node => <option key={node.id} value={node.id}>
                            {node.label} ({nodeOutputName(node, recipeLookup)})
                        </option>)}
                    </L.Select>
                </label>
                <label>
                    To
                    <L.Select value={connectionDraft.toId} onChange={e => setConnectionDraft(draft => ({...draft, toId: e.target.value}))}>
                        <option value="">Select target</option>
                        {connectionOptions.to.map(node => <option key={node.id} value={node.id}>{node.label}</option>)}
                    </L.Select>
                </label>
                <L.Button onClick={() => {
                    if (connectionDraft.fromId && connectionDraft.toId) {
                        createConnection(connectionDraft.fromId, connectionDraft.toId);
                    }
                }}>Create connection</L.Button>
            </L.ConnectionForm>
            <L.ConnectionList>
                {activeSetup.connections.map(conn => {
                    const fromNode = activeSetup.nodes.find(node => node.id === conn.fromId);
                    const toNode = activeSetup.nodes.find(node => node.id === conn.toId);
                    return <L.ConnectionRow key={conn.id}>
                        <span>{fromNode?.label ?? "?"} → {toNode?.label ?? "?"} ({GameItems[conn.itemId]?.name ?? conn.itemId})</span>
                        <L.SmallButton onClick={() => removeConnection(conn.id)}>Remove</L.SmallButton>
                    </L.ConnectionRow>;
                })}
                {!activeSetup.connections.length && <L.Hint>No connections yet. Link every ingredient into an assembler, then wire outputs to the inventory icon.</L.Hint>}
            </L.ConnectionList>
        </>
    );
}

function nodeOutputName(node: AssemblyNode, recipeLookup: Record<string, AssemblyRecipe>) {
    if (node.type === "source" && node.itemId) {
        return GameItems[node.itemId]?.name ?? "";
    }
    if (node.type === "assembler" && node.recipeId) {
        const recipeItem = recipeLookup[node.recipeId]?.result.itemId;
        return recipeItem ? (GameItems[recipeItem]?.name ?? "") : "";
    }
    return "";
}
